import React from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { CreateAddressRequest, CustomerAddress } from '../../types/user.types';

interface AddressFormProps {
    type: 'shipping' | 'billing';
    addressForm: CreateAddressRequest;
    setAddressForm: React.Dispatch<React.SetStateAction<CreateAddressRequest>>;
    editingAddress: CustomerAddress | null;
    onSubmit: (e: React.FormEvent) => Promise<void>;
    onCancel: () => void;
}

export const AddressForm: React.FC<AddressFormProps> = ({
    addressForm,
    setAddressForm,
    editingAddress,
    onSubmit,
    onCancel,
}) => {
    return (
        <form onSubmit={onSubmit} className="p-8 bg-gray-50/50 dark:bg-gray-900/50 rounded-3xl space-y-6 border border-border/40 shadow-inner">
            <h3 className="text-sm font-black uppercase tracking-widest text-[#111418] dark:text-white mb-2">
                {editingAddress ? 'Edit Address' : 'Add New Address'}
            </h3>

            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Address Label (e.g. Home, Work) *</Label>
                        <Input
                            type="text"
                            value={addressForm.label}
                            onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                            placeholder="Home"
                            required
                            maxLength={64}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Recipient Name *</Label>
                        <Input
                            type="text"
                            value={addressForm.name}
                            onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
                            placeholder="Full name"
                            required
                            maxLength={255}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Phone *</Label>
                        <Input
                            type="tel"
                            value={addressForm.phone}
                            onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                            placeholder="+1-555-0123"
                            required
                            maxLength={32}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Street Address *</Label>
                        <Input
                            type="text"
                            value={addressForm.line1}
                            onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })}
                            placeholder="Street address"
                            required
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Apt / Suite / Unit</Label>
                        <Input
                            type="text"
                            value={addressForm.line2}
                            onChange={(e) => setAddressForm({ ...addressForm, line2: e.target.value })}
                            placeholder="Apt 4B"
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">City *</Label>
                        <Input
                            type="text"
                            value={addressForm.city}
                            onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                            placeholder="City"
                            required
                            maxLength={128}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">State *</Label>
                        <Input
                            type="text"
                            value={addressForm.state}
                            onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                            placeholder="CA"
                            required
                            maxLength={128}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Postal Code *</Label>
                        <Input
                            type="text"
                            value={addressForm.postal_code}
                            onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                            placeholder="Zip code"
                            required
                            maxLength={32}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Country *</Label>
                        <Input
                            type="text"
                            value={addressForm.country}
                            onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value.toUpperCase() })}
                            placeholder="US"
                            required
                            maxLength={2}
                            className="h-12 bg-white dark:bg-black border-border/40 rounded-xl px-4 font-medium"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3 bg-white dark:bg-black p-4 rounded-xl border border-border/40 shadow-sm cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                        type="checkbox"
                        id="isDefault"
                        checked={addressForm.is_default}
                        onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                        className="size-5 rounded-md border-border text-primary focus:ring-primary"
                    />
                    <Label htmlFor="isDefault" className="text-xs font-bold uppercase tracking-widest cursor-pointer select-none">Set as default address</Label>
                </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
                <Button type="submit" className="flex-1 h-12 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20">
                    <Check className="size-4 mr-2" /> {editingAddress ? 'Update' : 'Save'} Address
                </Button>
                <Button type="button" variant="ghost" onClick={onCancel} className="h-12 px-6 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-400 hover:text-red-500 transition-colors">
                    <X className="size-4 mr-2" /> Cancel
                </Button>
            </div>
        </form>
    );
};
